import { AxiosInstance, AxiosResponse } from "axios"
import {
  ConversionOptions,
  ConversionType,
  EXPORT_VARIABLE_TYPES,
} from "types/engineExport.types"
/**
 * Conversion API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Converts data to Decipher XML format and triggers a download of the resulting XML file.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The identifier of the data to be converted.
 * @param options - Additional options for the conversion, including filename.
 * @returns The response object from the API call.
 * @throws Will throw an error if the conversion or download fails.
 */
export async function convertToDecipherXML(
  axiosInstance: AxiosInstance,
  id: string,
  options: ConversionOptions
): Promise<AxiosResponse<Blob>> {
  const config = {
    responseType: "blob" as const, // This is important to handle the file download
  }

  const response = await axiosInstance.post(
    `/convert-decipher-xml`,
    { id: id, options: options },
    config
  )

  // Create a URL for the blob
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", `${options.filename}.xml`) // Set the filename
  document.body.appendChild(link)
  link.click()
  link.remove()

  return response
}

/**
 * Converts data to HTML format (for preview).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The identifier of the data to be converted.
 * @param options - Additional options for the conversion.
 * @param variableNames - Optional variable name format ('numbering' or 'labels').
 * @returns The response object from the API call containing HTML.
 * @throws Will throw an error if the conversion fails.
 */
export async function convertToWord(
  axiosInstance: AxiosInstance,
  id: string,
  options: ConversionOptions,
  variableNames?: EXPORT_VARIABLE_TYPES
): Promise<AxiosResponse<string>> {
  const config = {
    params: {} as Record<string, string>,
    responseType: "text" as const,
  }

  // Always use HTML format for preview
  config.params.format = "html"

  if (variableNames) {
    config.params.var = variableNames
  }

  const response = await axiosInstance.post(
    `/convert-word`,
    { id: id, options: options },
    config
  )

  return response
}

/**
 * Downloads questionnaire as HTML or Word format.
 * Backend handles all formatting - Word documents include branding, author, version.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The identifier of the data to be converted.
 * @param options - Additional options for the conversion.
 * @param filename - The desired filename for the downloaded file.
 * @param type - The type of conversion - 'word' or 'html'.
 * @param variableNames - The type of variable names to export.
 * @returns The response object from the API call.
 * @throws Will throw an error if the conversion or download fails.
 */
export async function downloadHTML(
  axiosInstance: AxiosInstance,
  id: string,
  options: ConversionOptions,
  filename: string,
  type: ConversionType,
  variableNames: EXPORT_VARIABLE_TYPES
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
      params: {} as Record<string, string>,
      responseType: "text" as const,
    }

    // Set format based on download type - backend handles Word XML wrapping
    config.params.format = type === "word" ? "word" : "html"

    if (variableNames) {
      config.params.var = variableNames
    }

    const response = await axiosInstance.post(
      "/convert-word",
      { id: id, options: options },
      config
    )

    // Backend returns ready-to-use content (HTML or Word XML)
    const content = response.data

    let blob: Blob
    let fileExtension: string

    if (type === "word") {
      // Backend returns complete Word document - just download it
      blob = new Blob(["\ufeff", content], {
        type: "application/msword",
      })
      fileExtension = "doc"
    } else {
      // Save as HTML
      blob = new Blob([content], { type: "text/html" })
      fileExtension = "html"
    }

    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", `${filename}.${fileExtension}`)
    document.body.appendChild(link)
    link.click()

    // Clean up
    window.URL.revokeObjectURL(url)
    link.remove()

    return response
  } catch (error) {
    console.error("Error in downloadFile:", error)
    throw error
  }
}
